/** A campaign: several scenarios in a row, and what a hero carries between them.
 *
 * A campaign is not a rule and it is not a game. It is a *container* - an
 * ordered list of scenarios, a briefing for each, a choice of opening bonus,
 * and a statement of what survives the end of one map into the beginning of the
 * next - so it lives beside the scenario format rather than inside the kernel.
 * Nothing here is reachable from `executeCommand`; a campaign is assembled into
 * a `NewGameSetup`, handed to `newGame`, and then forgotten until that game is
 * won.
 *
 * The one thing that needs care is the carry-over, and the reason is that it is
 * the only place in this codebase where one game's state writes into another's.
 * The rule kept here is that it may only ever *copy forward what a player
 * earned*: experience, secondary skills, the artifacts he was carrying. It may
 * not copy a position, a movement pool, a mana total or an army - those belong
 * to the map that produced them, and a hero who arrives on a new continent with
 * yesterday's four thousand movement points is a bug that would take a week to
 * find.
 */

import type { ContentRegistry } from "../content/registry.js";
import type { Result } from "../result.js";
import { Err } from "../result.js";
import type { ArtifactDefId, HeroDefId, PlayerId } from "../ids.js";
import type { GameState, HeroState, Resources } from "../state.js";
import { RESOURCES } from "../state.js";
import { entityKeys } from "../util/iter.js";
import type { NewGameSetup } from "../adventure/create.js";
import { decodeTree, parseJson, serialize, encodeTree, type Json } from "./codec.js";
import type { PersistError } from "./types.js";
import { malformedFile } from "./types.js";
import { validateScenarioSetup } from "./scenario.js";

export const CAMPAIGN_KIND = "realm.campaign";
export const CAMPAIGN_FORMAT_VERSION = 1;

// ------------------------------------------------------------------ shapes

/** What a hero brings with him to the next map. */
export interface CarryRule {
  /**
   * How many of the player's heroes travel, best first by experience.
   *
   * 0 is a scenario that starts fresh. The classic's campaigns carry one or two.
   */
  readonly heroes: number;
  readonly experience: boolean;
  readonly skills: boolean;
  readonly artifacts: boolean;
  /** Spells the hero had learned; a spellbook is an artifact and travels with it. */
  readonly spells: boolean;
}

export const NO_CARRY: CarryRule = Object.freeze({
  heroes: 0, experience: false, skills: false, artifacts: false, spells: false,
});

export const CARRY_ALL: CarryRule = Object.freeze({
  heroes: 1, experience: true, skills: true, artifacts: true, spells: true,
});

/**
 * An opening bonus, chosen before the scenario starts.
 *
 * The classic offers two or three per scenario and the choice is the interesting part
 * of a campaign's structure: a player who takes the artifact is playing a
 * different opening from one who took the gold.
 */
export type CampaignBonus =
  | { readonly kind: "resources"; readonly label: string; readonly resources: Partial<Resources> }
  | { readonly kind: "artifact"; readonly label: string; readonly artifact: ArtifactDefId }
  | {
    readonly kind: "creatures"; readonly label: string;
    readonly creature: string; readonly count: number;
  };

export interface CampaignScenario {
  readonly id: string;
  readonly name: string;
  /** What the player is told before he chooses. Prose, not a rule. */
  readonly briefing: string;
  /** The map, in full. A campaign file is self-contained by design. */
  readonly setup: NewGameSetup;
  readonly bonuses: readonly CampaignBonus[];
  /** What the *previous* scenario's heroes bring into this one. */
  readonly carry: CarryRule;
}

export interface CampaignMeta {
  readonly name: string;
  readonly description: string;
  readonly author: string;
}

export interface CampaignFile {
  readonly kind: typeof CAMPAIGN_KIND;
  readonly formatVersion: number;
  readonly contentVersion: string;
  readonly meta: CampaignMeta;
  readonly scenarios: readonly CampaignScenario[];
}

export const CAMPAIGN_KEYS =
  ["contentVersion", "formatVersion", "kind", "meta", "scenarios"] as const;
export const SCENARIO_KEYS_IN_CAMPAIGN =
  ["bonuses", "briefing", "carry", "id", "name", "setup"] as const;

// ---------------------------------------------------------------- progress

/**
 * How far through a campaign a player is, and what he is bringing.
 *
 * Held by the host and not by the kernel: it is a save-game concern, and the
 * heroes in it are *snapshots taken at the moment a scenario was won* rather
 * than live entities. Storing whole `HeroState`s rather than ids is what makes
 * the chain work at all - the hero that finished scenario one does not exist in
 * scenario two's state until this puts him there.
 */
export interface CarriedHero {
  readonly def: HeroDefId;
  readonly experience: number;
  readonly primary: HeroState["primary"];
  readonly secondary: HeroState["secondary"];
  readonly artifacts: readonly ArtifactDefId[];
  readonly spells: HeroState["spellbook"];
}

export interface CampaignProgress {
  readonly campaign: string;
  /** Index of the next scenario to play. Equal to the length when finished. */
  readonly at: number;
  readonly carried: readonly CarriedHero[];
  /** Bonus chosen for each scenario played, by index; for the after-action log. */
  readonly bonuses: readonly string[];
}

export const startProgress = (campaign: string): CampaignProgress =>
  ({ campaign, at: 0, carried: [], bonuses: [] });

export const campaignFinished = (file: CampaignFile, p: CampaignProgress): boolean =>
  p.at >= file.scenarios.length;

export const currentScenario = (
  file: CampaignFile, p: CampaignProgress,
): CampaignScenario | null => file.scenarios[p.at] ?? null;

// -------------------------------------------------------------- carry-over

/**
 * Snapshot the heroes a finished scenario sends forward.
 *
 * Best first by experience, because a rule that took "the first by id" would
 * make the carry-over depend on which hero happened to be minted first, and a
 * player who won with his second hero would arrive with his first.
 */
export function carryFrom(
  state: GameState, player: PlayerId, rule: CarryRule,
): readonly CarriedHero[] {
  if (rule.heroes <= 0) return [];
  const mine: HeroState[] = [];
  for (const id of entityKeys(state.heroes)) {
    const h = state.heroes[id]!;
    if (h.owner === player) mine.push(h);
  }
  mine.sort((a, b) => b.experience - a.experience || (a.id < b.id ? -1 : 1));

  return mine.slice(0, rule.heroes).map((h) => ({
    def: h.def,
    experience: rule.experience ? h.experience : 0,
    primary: rule.experience ? h.primary : { attack: 0, defense: 0, spellPower: 0, knowledge: 0 },
    secondary: rule.skills ? h.secondary : [],
    artifacts: rule.artifacts ? equippedAndCarried(h) : [],
    spells: rule.spells ? h.spellbook : [],
  }));
}

/** Everything a hero is carrying, worn or in the backpack, in a stable order. */
function equippedAndCarried(h: HeroState): readonly ArtifactDefId[] {
  const out: ArtifactDefId[] = [];
  for (const slot of Object.keys(h.artifacts).sort()) {
    const a = h.artifacts[slot];
    if (a !== null && a !== undefined) out.push(a);
  }
  for (const a of h.backpack) out.push(a);
  return out;
}

/**
 * Write the carried heroes onto the new game's own heroes.
 *
 * Matched by *definition*, not by id: `newGame` mints fresh `HeroId`s and the
 * one thing that survives between two states is which hero this is. A carried
 * hero the new scenario does not place is dropped rather than added, because
 * placing him would need a cell, and choosing one is the map author's job.
 *
 * What is deliberately not copied: position, facing, movement, mana, army,
 * `inTown`, `visited`, `boat` and every modifier. All of those belong to the
 * map that produced them.
 */
export function applyCarry(
  state: GameState, player: PlayerId, carried: readonly CarriedHero[],
  content: ContentRegistry,
): GameState {
  if (carried.length === 0) return state;
  const byDef = new Map<string, CarriedHero>();
  for (const c of carried) byDef.set(c.def as string, c);

  const heroes: Record<string, HeroState> = { ...state.heroes };
  let changed = false;
  for (const id of entityKeys(state.heroes)) {
    const h = heroes[id as string]!;
    if (h.owner !== player) continue;
    const c = byDef.get(h.def as string);
    if (c === undefined) continue;

    const artifacts: Record<string, ArtifactDefId | null> = { ...h.artifacts };
    const backpack: ArtifactDefId[] = [...h.backpack];
    for (const a of c.artifacts) {
      const def = content.artifacts.get(a);
      if (def === undefined) continue;
      // Into its slot if the slot is free, into the backpack otherwise. A
      // carried artifact must never displace one the scenario gave him.
      if (artifacts[def.slot] === null || artifacts[def.slot] === undefined) {
        artifacts[def.slot] = a;
      } else if (!backpack.includes(a)) {
        backpack.push(a);
      }
    }

    heroes[id as string] = {
      ...h,
      experience: Math.max(h.experience, c.experience),
      primary: {
        attack: Math.max(h.primary.attack, c.primary.attack),
        defense: Math.max(h.primary.defense, c.primary.defense),
        spellPower: Math.max(h.primary.spellPower, c.primary.spellPower),
        knowledge: Math.max(h.primary.knowledge, c.primary.knowledge),
      },
      secondary: c.secondary.length > 0 ? c.secondary : h.secondary,
      artifacts,
      backpack,
      spellbook: [...new Set([...h.spellbook, ...c.spells])].sort(),
    };
    changed = true;
  }
  return changed ? { ...state, heroes: heroes as GameState["heroes"] } : state;
}

// ------------------------------------------------------------------ bonus

/**
 * Apply the opening bonus the player chose.
 *
 * Resources go to the treasury; an artifact and a stack of creatures go to the
 * player's first hero by id, and are simply lost if he has none - a scenario
 * that offers a creature bonus and places no hero is an authoring mistake the
 * campaign screen reports rather than a rule this has to invent an answer for.
 */
export function applyBonus(
  state: GameState, player: PlayerId, bonus: CampaignBonus | null,
  content: ContentRegistry,
): GameState {
  if (bonus === null) return state;
  const p = state.players[player];
  if (p === undefined) return state;

  if (bonus.kind === "resources") {
    const resources: Record<string, number> = { ...p.resources };
    for (const r of RESOURCES) resources[r] = (resources[r] ?? 0) + (bonus.resources[r] ?? 0);
    return {
      ...state,
      players: {
        ...state.players,
        [player]: { ...p, resources: resources as unknown as Resources },
      } as GameState["players"],
    };
  }

  const heroId = [...p.heroes].sort()[0];
  if (heroId === undefined) return state;
  const h = state.heroes[heroId];
  if (h === undefined) return state;

  if (bonus.kind === "artifact") {
    if (content.artifacts.get(bonus.artifact) === undefined) return state;
    return {
      ...state,
      heroes: {
        ...state.heroes,
        [heroId]: { ...h, backpack: [...h.backpack, bonus.artifact] },
      } as GameState["heroes"],
    };
  }

  const army = state.armies[h.army];
  if (army === undefined || bonus.count <= 0) return state;
  const slots = [...army.slots];
  const at = slots.findIndex((s) => s !== null && s.def === (bonus.creature as never));
  if (at >= 0) {
    const s = slots[at]!;
    slots[at] = { def: s.def, count: s.count + bonus.count };
  } else {
    const free = slots.findIndex((s) => s === null);
    if (free < 0) return state;
    slots[free] = { def: bonus.creature as never, count: bonus.count };
  }
  return {
    ...state,
    armies: { ...state.armies, [h.army]: { ...army, slots } } as GameState["armies"],
  };
}

// ------------------------------------------------------------------- file

export const serializeCampaign = (file: CampaignFile): string => serialize(encodeTree(file));
export const campaignToJson = (file: CampaignFile): Json => encodeTree(file);

const isObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

export function parseCampaign(text: string): Result<CampaignFile, PersistError> {
  const raw = parseJson(text);
  if (!raw.ok) return raw;
  const decoded = decodeTree(raw.value);
  if (!decoded.ok) return decoded;
  return campaignFromTree(decoded.value);
}

export function campaignFromTree(v: unknown): Result<CampaignFile, PersistError> {
  if (!isObject(v)) return Err(malformedFile("the root of a campaign is an object"));
  if (v["kind"] !== CAMPAIGN_KIND) {
    return Err(malformedFile(
      `this is not a campaign: kind is ${JSON.stringify(v["kind"])}`,
    ));
  }
  const version = v["formatVersion"];
  if (typeof version !== "number" || !Number.isInteger(version)) {
    return Err(malformedFile("formatVersion is not an integer"));
  }
  if (version > CAMPAIGN_FORMAT_VERSION) {
    return Err(malformedFile(
      `this campaign was written by a newer build (format ${String(version)})`,
    ));
  }
  const meta = v["meta"];
  if (!isObject(meta) || typeof meta["name"] !== "string") {
    return Err(malformedFile("meta.name is required"));
  }
  const list = v["scenarios"];
  if (!Array.isArray(list) || list.length === 0) {
    return Err(malformedFile("a campaign needs at least one scenario"));
  }

  const scenarios: CampaignScenario[] = [];
  const ids = new Set<string>();
  for (const [i, raw2] of list.entries()) {
    if (!isObject(raw2)) return Err(malformedFile(`scenarios[${String(i)}] is not an object`));
    const id = raw2["id"];
    if (typeof id !== "string" || id === "") {
      return Err(malformedFile(`scenarios[${String(i)}].id is missing`));
    }
    if (ids.has(id)) return Err(malformedFile(`scenarios: ${id} appears twice`));
    ids.add(id);
    const setup = validateScenarioSetup(raw2["setup"]);
    if (!setup.ok) {
      // The scenario's own complaint, prefixed with which scenario it was
      // about: "setup.map.terrain has 4096 cells" is unhelpful in a file with
      // six maps in it.
      const why = setup.error.kind === "MALFORMED"
        ? setup.error.issues.join("; ")
        : setup.error.kind;
      return Err(malformedFile(`scenarios[${String(i)}] (${id}): ${why}`));
    }
    scenarios.push({
      id,
      name: typeof raw2["name"] === "string" ? raw2["name"] : id,
      briefing: typeof raw2["briefing"] === "string" ? raw2["briefing"] : "",
      setup: setup.value,
      bonuses: readBonuses(raw2["bonuses"]),
      carry: readCarry(raw2["carry"]),
    });
  }

  return {
    ok: true,
    value: {
      kind: CAMPAIGN_KIND,
      formatVersion: version,
      contentVersion: typeof v["contentVersion"] === "string" ? v["contentVersion"] : "",
      meta: {
        name: meta["name"],
        description: typeof meta["description"] === "string" ? meta["description"] : "",
        author: typeof meta["author"] === "string" ? meta["author"] : "",
      },
      scenarios,
    },
  };
}

/** A bonus this build does not understand is dropped, not refused: a campaign
 *  with one unreadable choice is still a campaign. */
function readBonuses(v: unknown): readonly CampaignBonus[] {
  if (!Array.isArray(v)) return [];
  const out: CampaignBonus[] = [];
  for (const b of v) {
    if (!isObject(b)) continue;
    const label = typeof b["label"] === "string" ? b["label"] : "";
    if (b["kind"] === "resources" && isObject(b["resources"])) {
      const res: Record<string, number> = {};
      for (const r of RESOURCES) {
        const n = (b["resources"] as Record<string, unknown>)[r];
        if (typeof n === "number" && Number.isInteger(n)) res[r] = n;
      }
      out.push({ kind: "resources", label, resources: res as Partial<Resources> });
    } else if (b["kind"] === "artifact" && typeof b["artifact"] === "string") {
      out.push({ kind: "artifact", label, artifact: b["artifact"] as ArtifactDefId });
    } else if (
      b["kind"] === "creatures" && typeof b["creature"] === "string"
      && typeof b["count"] === "number"
    ) {
      out.push({ kind: "creatures", label, creature: b["creature"], count: b["count"] });
    }
  }
  return out;
}

function readCarry(v: unknown): CarryRule {
  if (!isObject(v)) return NO_CARRY;
  const n = v["heroes"];
  return {
    heroes: typeof n === "number" && Number.isInteger(n) && n > 0 ? n : 0,
    experience: v["experience"] === true,
    skills: v["skills"] === true,
    artifacts: v["artifacts"] === true,
    spells: v["spells"] === true,
  };
}
